/**
 * FeedbackAnalytics Component
 *
 * Analytics dashboard for feature requests with charts and metrics.
 * Admin-only component.
 *
 * @module components/dev/FeedbackAnalytics
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import { RequestAnalytics, STATUS_BADGES, REQUEST_TYPES } from '../../types/featureRequest';

/**
 * FeedbackAnalytics - Analytics dashboard
 */
const FeedbackAnalytics: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();

  // State
  const [analytics, setAnalytics] = useState<RequestAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch analytics
   */
  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await FeatureRequestService.getAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  if (user && user.role !== 'admin') {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="error">
          Access Denied: You must be an administrator to access this page.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !analytics) {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="error">{error || 'No analytics data available'}</Alert>
        <Button onClick={() => navigate('/dev/feedback-dashboard')} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  // Prepare data for charts
  const typeData = Object.entries(analytics.byType).map(([type, count]) => ({
    name: REQUEST_TYPES[type as keyof typeof REQUEST_TYPES].label,
    value: count,
    color: REQUEST_TYPES[type as keyof typeof REQUEST_TYPES].color,
  }));

  const statusData = Object.entries(analytics.byStatus).map(([status, count]) => ({
    name: STATUS_BADGES[status as keyof typeof STATUS_BADGES].label,
    value: count,
  }));

  const COLORS = ['#2196F3', '#F44336', '#FF9800', '#4CAF50', '#9E9E9E', '#FFC107'];

  return (
    <Box maxWidth={1400} mx="auto" p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Feature request metrics and insights
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/dev/feedback-dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <TrendingIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{analytics.totalRequests}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <CompleteIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4">{analytics.completionRate.toFixed(0)}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <MessageIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4">{analytics.responseRate.toFixed(0)}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Response Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <PendingIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4">{analytics.averageResolutionTime.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Days to Resolve
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} mb={4}>
        {/* Requests by Type */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Requests by Type
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={typeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {typeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Requests by Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Requests by Status
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusData}>
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2196F3">
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Most Active Users */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Most Active Users
        </Typography>
        <Stack spacing={2}>
          {analytics.mostActiveUsers.slice(0, 5).map((user, index) => (
            <Box
              key={user.userId}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
              sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={`#${index + 1}`} color="primary" size="small" />
                <Typography variant="body1">{user.userName}</Typography>
              </Stack>
              <Chip label={`${user.requestCount} requests`} variant="outlined" />
            </Box>
          ))}
          {analytics.mostActiveUsers.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No user data available yet
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default FeedbackAnalytics;
